import React from "react";
import CalendarDayCell from "./CalendarDayCell";

interface CalendarGridProps {
  currentDate: Date;
  onDateClick: (date: Date) => void;
  timelineData?: any; // Accepts whole response or response.data
  onEmptySlotClick?: (time: string, dateStr: string) => void;
}

const weekDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// Guarantees exact YYYY-MM-DD match (e.g. 2026-07-09, 2026-07-28)
const formatLocalDateISO = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const CalendarGrid: React.FC<CalendarGridProps> = ({
  currentDate,
  onDateClick,
  timelineData,
  onEmptySlotClick,
}) => {
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  // First day of current month
  const firstDay = new Date(year, month, 1);
  const startDay = firstDay.getDay();

  // Number of days in current & previous month
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPreviousMonth = new Date(year, month, 0).getDate();

  const calendarDays: {
    date: number;
    currentMonth: boolean;
    fullDate: Date;
  }[] = [];

  // Previous month's dates
  for (let i = startDay - 1; i >= 0; i--) {
    calendarDays.push({
      date: daysInPreviousMonth - i,
      currentMonth: false,
      fullDate: new Date(year, month - 1, daysInPreviousMonth - i),
    });
  }

  // Current month's dates
  for (let i = 1; i <= daysInMonth; i++) {
    calendarDays.push({
      date: i,
      currentMonth: true,
      fullDate: new Date(year, month, i),
    });
  }

  // Next month's dates
  while (calendarDays.length < 42) {
    const nextDate = calendarDays.length - (startDay + daysInMonth) + 1;
    calendarDays.push({
      date: nextDate,
      currentMonth: false,
      fullDate: new Date(year, month + 1, nextDate),
    });
  }

  // Safe unwrap regardless of whether timelineData is `response` or `response.data`
  const apiDays: any[] =
    timelineData?.days || timelineData?.data?.days || [];

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
      {/* Week Header */}
      <div className="grid grid-cols-7 bg-slate-50">
        {weekDays.map((day) => (
          <div key={day} className="py-3 text-center font-bold text-slate-600 border-b">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7">
        {calendarDays.map((day, index) => {
          const dateKey = formatLocalDateISO(day.fullDate);

          // Find exact matching day object from response `days` array
          const matchedDay = apiDays.find((d: any) => d.date === dateKey);
          const services = matchedDay?.bookings || [];
          const isWorkingDay = matchedDay ? matchedDay.isWorkingDay : true;

          return (
            <CalendarDayCell
              key={index}
              day={day}
              services={services}
              isWorkingDay={isWorkingDay}
              onDateClick={onDateClick}
              onEmptySlotClick={onEmptySlotClick}
            />
          );
        })}
      </div>
    </div>
  );
};

export default CalendarGrid;